import { router } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import {
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/FontAwesome";
import { useDispatch } from "react-redux";

import FooterComponent from "../component/footer";
import { login } from "../redux/actions/authactions";
import { useLanguage } from "./LanguageProvider";

export default function Login() {
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const LogoIMG = require("../../assets/logowhite.png");

  const [isFocused, setIsFocused] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalSuccessVisible, setModalSuccessVisible] = useState(false);
  const [empcode, setEmpcode] = useState("");

  const [softKeyboardEnabled, setSoftKeyboardEnabled] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const inputRef = useRef<TextInput>(null);
  const keyboardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const keyboardShowSubscription = Keyboard.addListener(
      "keyboardDidShow",
      () => {
        setIsKeyboardVisible(true);
      }
    );

    const keyboardHideSubscription = Keyboard.addListener(
      "keyboardDidHide",
      () => {
        setIsKeyboardVisible(false);
        setSoftKeyboardEnabled(false);
      }
    );

    return () => {
      keyboardShowSubscription.remove();
      keyboardHideSubscription.remove();

      if (keyboardTimerRef.current) {
        clearTimeout(keyboardTimerRef.current);
      }
    };
  }, []);

  const clearKeyboardTimer = () => {
    if (keyboardTimerRef.current) {
      clearTimeout(keyboardTimerRef.current);
      keyboardTimerRef.current = null;
    }
  };

  const openSoftKeyboard = () => {
    clearKeyboardTimer();

    inputRef.current?.blur();
    setSoftKeyboardEnabled(true);

    keyboardTimerRef.current = setTimeout(() => {
      inputRef.current?.focus();
    }, Platform.OS === "android" ? 180 : 100);
  };

  const closeSoftKeyboard = () => {
    clearKeyboardTimer();

    setSoftKeyboardEnabled(false);
    setIsKeyboardVisible(false);

    Keyboard.dismiss();
    inputRef.current?.blur();

    keyboardTimerRef.current = setTimeout(() => {
      inputRef.current?.focus();
    }, Platform.OS === "android" ? 180 : 100);
  };

  const toggleSoftKeyboard = () => {
    if (softKeyboardEnabled || isKeyboardVisible) {
      closeSoftKeyboard();
    } else {
      openSoftKeyboard();
    }
  };

  const dismissKeyboard = () => {
    clearKeyboardTimer();

    setSoftKeyboardEnabled(false);
    setIsKeyboardVisible(false);

    Keyboard.dismiss();
    inputRef.current?.blur();
  };

  const handleSubmit = async () => {
    const employeeCode = empcode.trim();

    if (!employeeCode) {
      dismissKeyboard();
      setModalVisible(true);
      return;
    }

    try {
      const response = await dispatch(login(employeeCode) as any);

      dismissKeyboard();

      if (response?.success === true) {
        // [เพิ่มโค้ดที่นี่] บันทึก Session ลง localStorage สำหรับ Web ป้องกันการหลุดเมื่อ Refresh
        if (Platform.OS === "web") {
          localStorage.setItem("empcode", employeeCode);
          // ตั้งเวลาหมดอายุ 1 ชั่วโมง (60 * 60 * 1000 ms) ให้ตรงกับฝั่ง Layout
          localStorage.setItem("sessionExpiry", String(Date.now() + 60 * 60 * 1000));
        }
        
        setModalSuccessVisible(true);
      } else {
        console.log("Login Failed:", response?.message);
        setModalVisible(true);
      }
    } catch (error) {
      console.log("Login Error:", error);

      dismissKeyboard();
      setModalVisible(true);
    }
  };

  return (
    <>
      <KeyboardAvoidingView
        className="flex-1 items-center bg-white"
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <Pressable className="absolute inset-0" onPress={dismissKeyboard} />

        <View
          pointerEvents="none"
          className="w-full h-[45%] bg-[#F17736] rounded-b-[20px] items-center justify-center"
        >
          <Image
            source={LogoIMG}
            resizeMode="contain"
            style={{
              width: isFocused ? "22%" : "35%",
              height: isFocused ? "32%" : "34%",
            }}
          />
        </View>

        <View className="absolute top-[36%] w-4/5 bg-white p-5 rounded-xl items-center shadow-lg">
          <Text className="text-[#F17736] text-3xl my-5 font-kanit">
            {t("login")}
          </Text>

          <Text className="text-gray-500 text-xl mb-5 font-kanit">
            {t("loginWelcome")}
          </Text>

          <TextInput
            ref={inputRef}
            className="w-full h-12 border border-gray-300 rounded-lg px-4 bg-gray-50 font-kanit"
            placeholder={t("empCode")}
            placeholderTextColor="#9CA3AF"
            value={empcode}
            onChangeText={setEmpcode}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onSubmitEditing={handleSubmit}
            returnKeyType="done"
            autoCapitalize="none"
            autoCorrect={false}
            showSoftInputOnFocus={softKeyboardEnabled}
          />

          <TouchableOpacity
            activeOpacity={0.7}
            onPress={toggleSoftKeyboard}
            className="flex-row items-center justify-center p-3 mt-2"
          >
            <Icon
              name="keyboard-o"
              size={20}
              color={
                softKeyboardEnabled || isKeyboardVisible
                  ? "#DC2626"
                  : "#F17736"
              }
            />
            <Text className="ml-2 text-sm text-gray-600 font-kanit">
              {softKeyboardEnabled || isKeyboardVisible
                ? "ปิดคีย์บอร์ดบนจอ"
                : "เปิดคีย์บอร์ดบนจอ"}
            </Text>
          </TouchableOpacity>

          <Text className="text-black text-sm my-5 font-kanit">
            {t("contactAdmin")}{" "}
            <Text className="text-blue-500">Digital Transformation.</Text>
            <Text className="text-blue-500">SPL 2.2.8</Text>
          </Text>
        </View>
      </KeyboardAvoidingView>

      <FooterComponent />

      {/* Error Modal */}
      <Modal
        animationType="fade"
        transparent
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="exclamation-circle" size={28} color="#ec7063" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("error")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("loginFailed")}
            </Text>

            <View className="flex-row justify-around">
              <Pressable
                className="flex-1 bg-[#58d68d] py-2 rounded-lg mx-1"
                onPress={() => {
                  setModalVisible(false);
                  setEmpcode("");

                  setSoftKeyboardEnabled(false);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 200);
                }}
              >
                <Text className="text-center text-white font-kanit">
                  {t("confirm")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* Success Modal */}
      <Modal
        animationType="fade"
        transparent
        visible={modalSuccessVisible}
        onRequestClose={() => setModalSuccessVisible(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="check-circle" size={28} color="#58d68d" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("success")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("loginSuccess")}
            </Text>

            <View className="flex-row justify-around">
              <Pressable
                className="flex-1 bg-[#58d68d] py-3 rounded-lg mx-1"
                onPress={() => {
                  setModalSuccessVisible(false);
                  router.replace("/screens/home");
                }}
              >
                <Text className="text-center text-white font-kanit">
                  {t("confirm")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}